import { useId } from "react"

const DataWarehouseIllustration = ({ paused = false }: { paused?: boolean }) => {
  const id = useId()
  const glowId = `dw-glow-${id}`
  const clipId = `dw-db-clip-${id}`

  return (
    <svg
      width="379"
      height="281"
      viewBox="0 0 379 281"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-fg-secondary opacity-40"
      style={{ animationPlayState: paused ? "paused" : "running" }}
    >
      <defs>
        {/* Glow filter for traveling dots */}
        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Clip path matching the database cylinder outline */}
        <clipPath id={clipId}>
          <path d="M250.877 56C219.605 56 194.254 63.166 194.254 72.004V72.004L191.792 75.697V113.855L194.254 117.548V117.548L191.792 121.24V158.167L194.254 161.86V161.86L191.792 165.552V203.711L194.254 207.404C194.254 211.648 200.219 215.718 210.838 218.719C221.457 221.72 235.859 223.406 250.876 223.406C265.894 223.406 280.296 221.72 290.915 218.719C301.533 215.718 307.499 211.648 307.499 207.404L309.961 203.711V165.552L307.499 161.86V161.86L309.961 158.167V121.24L307.499 117.548V117.548L309.961 113.855V75.697L307.499 72.002C307.499 63.164 282.149 56 250.877 56Z" />
        </clipPath>
      </defs>

      <style>
        {`
        @keyframes dw-travel-top {
          0%, 15% { offset-distance: 0%; opacity: 0; }
          20% { opacity: 1; }
          45% { opacity: 1; }
          50% { offset-distance: 100%; opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes dw-travel-mid {
          0%, 30% { offset-distance: 0%; opacity: 0; }
          35% { opacity: 1; }
          60% { opacity: 1; }
          65% { offset-distance: 100%; opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes dw-travel-bot {
          0%, 50% { offset-distance: 0%; opacity: 0; }
          55% { opacity: 1; }
          80% { opacity: 1; }
          85% { offset-distance: 100%; opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes dw-db-pulse {
          0%, 40% { opacity: 0; }
          50% { opacity: 0.15; }
          60% { opacity: 0; }
          75% { opacity: 0.1; }
          85% { opacity: 0; }
          100% { opacity: 0; }
        }
        .dw-dot-top {
          offset-path: path('M98 78.9336C137.6 78.9336 137.6 100.934 178 100.934');
          animation: dw-travel-top 4.5s ease-in-out infinite;
          animation-play-state: inherit;
        }
        .dw-dot-mid {
          offset-path: path('M98 139.434L179 139.434');
          animation: dw-travel-mid 4.5s ease-in-out infinite;
          animation-play-state: inherit;
        }
        .dw-dot-bot {
          offset-path: path('M98 201.934C137.6 201.934 137.6 180.934 178 180.934');
          animation: dw-travel-bot 4.5s ease-in-out infinite;
          animation-play-state: inherit;
        }
        .dw-db-glow-rect {
          animation: dw-db-pulse 4.5s ease-in-out infinite;
          animation-play-state: inherit;
        }
      `}
      </style>

      {/* Database cylinder */}
      <path
        d="M194.254 117.547L191.792 121.24V158.167L194.254 161.86C194.254 166.104 200.219 170.174 210.838 173.175C221.457 176.176 235.859 177.862 250.876 177.862C265.894 177.862 280.296 176.176 290.915 173.175C301.533 170.174 307.499 166.104 307.499 161.86L309.961 158.167V121.24L307.499 117.547"
        stroke="currentColor"
      />
      <path
        d="M194.254 161.859L191.792 165.552V203.711L194.254 207.404C194.254 211.648 200.219 215.718 210.838 218.719C221.457 221.72 235.859 223.406 250.876 223.406C265.894 223.406 280.296 221.72 290.915 218.719C301.533 215.718 307.499 211.648 307.499 207.404L309.961 203.711V165.552L307.499 161.859"
        stroke="currentColor"
      />
      <path
        d="M307.499 72.002C307.499 80.8397 282.149 88.0041 250.877 88.0041C219.605 88.0041 194.254 80.8416 194.254 72.0039M307.499 72.002C307.499 63.1644 282.149 56 250.877 56C219.605 56 194.254 63.1662 194.254 72.0039M307.499 72.002L307.499 72.0039L309.961 75.6967V113.855L307.499 117.548C307.499 121.792 301.533 125.862 290.915 128.863C280.296 131.864 265.894 133.55 250.876 133.55C235.859 133.55 221.457 131.864 210.838 128.863C200.219 125.862 194.254 121.792 194.254 117.548L191.792 113.855V75.6967L194.254 72.0039"
        stroke="currentColor"
      />

      {/* Database glow overlay — clipped to cylinder shape */}
      <g clipPath={`url(#${clipId})`}>
        <rect
          className="dw-db-glow-rect"
          x="188"
          y="52"
          width="126"
          height="176"
          fill="currentColor"
          opacity="0"
        />
      </g>

      {/* Blockchain icon — Ethereum diamond */}
      <circle cx="69.9307" cy="79" r="19.5" stroke="currentColor" />
      <path
        d="M69.9308 69.7695L75.4701 79.0016L69.9308 82.6944L64.3916 79.0016L69.9308 69.7695Z"
        fill="currentColor"
        opacity="0.8"
      />
      <path
        d="M69.9308 69.7695L64.3916 79.0016H69.9308V69.7695Z"
        fill="currentColor"
        opacity="0.7"
      />
      <path
        d="M69.9307 69.7695L75.4699 79.0016H69.9307V69.7695Z"
        fill="currentColor"
        opacity="0.9"
      />
      <path
        d="M69.9308 82.6928L75.4701 79L69.9308 88.2321L64.3916 79L69.9308 82.6928Z"
        fill="currentColor"
        opacity="0.5"
      />

      {/* Blockchain icon — Bars */}
      <circle cx="69.9307" cy="140.547" r="19.5" stroke="currentColor" />
      <path
        d="M62.5449 135.008H75.4698L77.3162 137.777H64.3913L62.5449 135.008Z"
        fill="currentColor"
      />
      <path
        d="M62.5449 143.316H75.4698L77.3162 146.086H64.3913L62.5449 143.316Z"
        fill="currentColor"
      />
      <path
        d="M77.3162 139.164H64.3913L62.5449 141.934H75.4698L77.3162 139.164Z"
        fill="currentColor"
      />

      {/* Blockchain icon — Bitcoin */}
      <circle cx="69.9307" cy="202.094" r="19.5" stroke="currentColor" />
      <path d="M68.5459 192.863H67.1611V196.556H68.5459V192.863Z" fill="currentColor" />
      <path d="M72.7002 192.863H71.3154V196.556H72.7002V192.863Z" fill="currentColor" />
      <path d="M68.5459 207.633H67.1611V211.326H68.5459V207.633Z" fill="currentColor" />
      <path d="M72.7002 207.633H71.3154V211.326H72.7002V207.633Z" fill="currentColor" />
      <path
        d="M65.3145 196.555H72.7001C75.4697 196.555 76.3929 198.401 76.3929 199.786C76.3929 201.171 75.4697 202.094 73.6233 202.094C75.9313 202.094 77.3161 203.017 77.3161 204.864C77.3161 207.172 75.4697 207.633 72.7001 207.633H65.3145V196.555ZM68.0841 198.401V201.171H71.7769C73.1617 201.171 73.6233 200.248 73.6233 199.786C73.6233 199.324 73.1617 198.401 71.7769 198.401H68.0841ZM68.0841 203.017V205.787H72.2385C73.6233 205.787 74.5465 205.325 74.5465 204.402C74.5465 203.479 73.6233 203.017 72.2385 203.017H68.0841Z"
        fill="currentColor"
      />

      {/* Dashed connection paths */}
      <path
        d="M98 201.934C137.6 201.934 137.6 180.934 178 180.934"
        stroke="currentColor"
        strokeDasharray="4.92 3.69"
      />
      <path
        d="M98 78.9336C137.6 78.9336 137.6 100.934 178 100.934"
        stroke="currentColor"
        strokeDasharray="4.92 3.69"
      />
      <line
        x1="98"
        y1="139.434"
        x2="179"
        y2="139.434"
        stroke="currentColor"
        strokeDasharray="4.92 3.69"
      />

      {/* Traveling dots */}
      <circle className="dw-dot-top" r="3" fill="currentColor" filter={`url(#${glowId})`} />
      <circle className="dw-dot-mid" r="3" fill="currentColor" filter={`url(#${glowId})`} />
      <circle className="dw-dot-bot" r="3" fill="currentColor" filter={`url(#${glowId})`} />
    </svg>
  )
}

export default DataWarehouseIllustration
